package com.gastoncantera.intercom

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlin.coroutines.resume
import kotlin.js.json
import kotlin.math.PI
import kotlin.math.asin
import kotlin.math.cos
import kotlin.math.min
import kotlin.math.sin
import kotlin.math.sqrt

private const val EARTH_RADIUS_METERS = 6_371_000.0
private const val GATE_PASS_KEY = "tb-location-gate-ok"

// Marca Timbre Bahia (logo / design)
private const val BLUE_DARK = "#0B3167"
private const val BLUE = "#1160BE"
private const val TEXT = "#1a2a3a"
private const val MUTED = "#5a6b7c"
private const val BG = "#ffffff"
private const val CARD = "#f3f7fc"
private const val LOGO_SRC = "logo.png"

private data class LatLng(val lat: Double, val lng: Double)

/**
 * Gate web: pantallas HTML propias (no Compose). El canvas de Skiko tapaba el overlay y parecía
 * spinner eterno; por eso el flujo interactivo es document completo + JS inline + reload al OK.
 */
actual suspend fun enforceLocationGate(targets: List<LocationGateTarget>) {
    if (targets.isEmpty()) return

    if (!isMobileDevice()) {
        showTerminalScreen(mobileRequiredHtml())
        awaitCancellation()
    }

    // Ya pasó el gate en esta pestaña: revalidar en silencio (llamadas del portero).
    if (hasGatePass()) {
        val pos = currentPositionOrNull()
        val allowed = pos != null && targets.any { distanceMeters(pos, it) <= it.radiusMeters }
        if (allowed) return
        clearGatePass()
    }

    // Página HTML+JS autónoma (no depende de coroutines ni del canvas).
    showTerminalScreen(interactiveLocationPage(targets), withMap = true)
    awaitCancellation()
}

private fun isMobileDevice(): Boolean {
    val nav = window.navigator
    val ua = nav.userAgent
    val uaMobile = Regex("Mobi|Android|iPhone|iPod|iPad|webOS|BlackBerry|IEMobile|Opera Mini", RegexOption.IGNORE_CASE)
        .containsMatchIn(ua)
    val touchPoints = (nav.asDynamic().maxTouchPoints as? Int) ?: 0
    val coarse = try {
        window.matchMedia("(pointer: coarse)").matches
    } catch (_: Throwable) {
        false
    }
    return uaMobile || touchPoints > 1 || coarse
}

private fun hasGatePass(): Boolean =
    try {
        window.sessionStorage.getItem(GATE_PASS_KEY) == "1"
    } catch (_: Throwable) {
        false
    }

private fun clearGatePass() {
    try {
        window.sessionStorage.removeItem(GATE_PASS_KEY)
    } catch (_: Throwable) {
    }
}

private suspend fun currentPositionOrNull(): LatLng? = suspendCancellableCoroutine { cont ->
    val geolocation = window.navigator.asDynamic().geolocation
    if (geolocation == null) {
        cont.resume(null)
        return@suspendCancellableCoroutine
    }
    geolocation.getCurrentPosition(
        { position: dynamic ->
            val lat = position.coords.latitude as Double
            val lng = position.coords.longitude as Double
            if (cont.isActive) cont.resume(LatLng(lat, lng))
        },
        { _: dynamic ->
            if (cont.isActive) cont.resume(null)
        },
        json("enableHighAccuracy" to true, "timeout" to 15_000, "maximumAge" to 10_000),
    )
}

private fun showTerminalScreen(bodyInner: String, withMap: Boolean = false) {
    window.stop()
    document.open()
    document.write(fullPage(bodyInner, withMap = withMap))
    document.close()
}

private fun fullPage(inner: String, withMap: Boolean = false): String {
    val mapHead = if (withMap) {
        """
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"
  integrity="sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=" crossorigin=""/>
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"
  integrity="sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=" crossorigin=""></script>
""".trimIndent()
    } else {
        ""
    }
    return """
<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Timbre Bahia</title>
<style>${gateCss()}</style>
$mapHead
</head>
<body>
$inner
</body>
</html>
""".trimIndent()
}

private fun interactiveLocationPage(targets: List<LocationGateTarget>): String {
    val targetsJson = targets.joinToString(separator = ",", prefix = "[", postfix = "]") { t ->
        """{"lat":${t.lat},"lng":${t.lng},"radius":${t.radiusMeters}}"""
    }
    return """
${locationExplainInner(interactive = true)}
<template id="tb-wrong-far">${wrongLocationInner(denied = false)}</template>
<template id="tb-wrong-denied">${wrongLocationInner(denied = true)}</template>
<script>
(function(){
  var TARGETS = $targetsJson;
  var PASS_KEY = "$GATE_PASS_KEY";
  var EARTH = $EARTH_RADIUS_METERS;
  function toRad(d){ return d * Math.PI / 180; }
  function dist(a, b){
    var dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
    var lat1 = toRad(a.lat), lat2 = toRad(b.lat);
    var h = Math.sin(dLat/2)*Math.sin(dLat/2) +
      Math.cos(lat1)*Math.cos(lat2)*Math.sin(dLng/2)*Math.sin(dLng/2);
    return 2 * EARTH * Math.asin(Math.min(1, Math.sqrt(h)));
  }
  function initMap(user){
    if (typeof L === "undefined") return;
    var el = document.getElementById("tb-map");
    if (!el) return;
    var map = L.map(el, { zoomControl: true, attributionControl: true });
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a>"
    }).addTo(map);
    var bounds = [];
    TARGETS.forEach(function(t){
      L.circle([t.lat, t.lng], {
        radius: t.radius, color: "#2895E7", fillColor: "#1160BE", fillOpacity: 0.12, weight: 1
      }).addTo(map);
      L.circleMarker([t.lat, t.lng], {
        radius: 8, color: "#0B3167", fillColor: "#1160BE", fillOpacity: 1, weight: 2
      }).bindPopup("Edificio").addTo(map);
      bounds.push([t.lat, t.lng]);
    });
    if (user) {
      L.circleMarker([user.lat, user.lng], {
        radius: 8, color: "#b71c1c", fillColor: "#e53935", fillOpacity: 1, weight: 2
      }).bindPopup("Tu ubicaci\u00f3n").addTo(map);
      bounds.push([user.lat, user.lng]);
    }
    if (bounds.length === 1) map.setView(bounds[0], 16);
    else map.fitBounds(bounds, { padding: [36, 36], maxZoom: 17 });
    setTimeout(function(){ map.invalidateSize(); }, 50);
  }
  function showWrong(id, user){
    var t = document.getElementById(id);
    if (!t) return;
    document.body.innerHTML = t.innerHTML;
    setTimeout(function(){ initMap(user || null); }, 0);
  }
  var btn = document.getElementById("tb-share-location");
  if (!btn) return;
  btn.addEventListener("click", function(){
    btn.disabled = true;
    btn.textContent = "Obteniendo ubicaci\u00f3n\u2026";
    if (!navigator.geolocation) {
      showWrong("tb-wrong-denied", null);
      return;
    }
    navigator.geolocation.getCurrentPosition(function(pos){
      var here = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      var ok = TARGETS.some(function(t){ return dist(here, t) <= t.radius; });
      if (ok) {
        try { sessionStorage.setItem(PASS_KEY, "1"); } catch (e) {}
        location.reload();
      } else {
        showWrong("tb-wrong-far", here);
      }
    }, function(){
      showWrong("tb-wrong-denied", null);
    }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 });
  });
})();
</script>
""".trimIndent()
}

private fun gateCss(): String = """
*{box-sizing:border-box}
body{margin:0;min-height:100vh;font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;
background:$BG;color:$TEXT;
background-image:radial-gradient(circle at 0 0,rgba(17,96,190,.06),transparent 42%),
radial-gradient(circle at 100% 100%,rgba(40,149,231,.07),transparent 40%)}
.wrap{min-height:100vh;display:flex;flex-direction:column;align-items:center;
padding:48px 24px 28px;text-align:center}
.icon{width:72px;height:72px;border-radius:50%;background:$BLUE;display:flex;align-items:center;
justify-content:center;margin:0 auto 22px;position:relative;box-shadow:0 8px 24px rgba(11,49,103,.18)}
.icon svg{width:34px;height:34px;fill:#fff}
.icon .badge{position:absolute;right:-2px;bottom:-2px;width:22px;height:22px;border-radius:50%;
background:#e53935;border:2px solid #fff;display:flex;align-items:center;justify-content:center}
.icon .badge svg{width:12px;height:12px}
h1{margin:0 0 10px;font-size:1.55rem;line-height:1.2;color:$BLUE_DARK;font-weight:700}
.lead{margin:0 0 22px;font-size:1rem;line-height:1.45;color:$TEXT;max-width:22rem}
.map-wrap{width:100%;max-width:22rem;margin:0 0 22px;text-align:left}
.map{height:168px;width:100%;border-radius:16px;overflow:hidden;border:1px solid rgba(17,96,190,.15);
z-index:0;background:$CARD}
.map-legend{display:flex;gap:14px;margin:8px 0 0;font-size:.75rem;color:$MUTED;flex-wrap:wrap}
.map-legend i{display:inline-block;width:8px;height:8px;border-radius:50%;margin-right:5px;vertical-align:middle}
.map-legend .you{background:#e53935}
.map-legend .building{background:#1160BE}
.card{width:100%;max-width:22rem;background:$CARD;border:1px solid rgba(17,96,190,.12);
border-radius:16px;padding:16px 18px;text-align:left;margin:0 0 24px}
.row{display:flex;gap:12px;align-items:flex-start;margin:0 0 12px;font-size:.92rem;line-height:1.35;color:$TEXT}
.row:last-child{margin:0}
.row .bullet{flex:0 0 28px;height:28px;border-radius:50%;background:#fff;border:1px solid rgba(17,96,190,.2);
display:flex;align-items:center;justify-content:center}
.row .bullet svg{width:15px;height:15px;fill:$BLUE}
.btn{appearance:none;border:0;border-radius:14px;background:$BLUE;color:#fff;font-weight:600;
font-size:1.05rem;padding:14px 20px;width:100%;max-width:22rem;cursor:pointer;
display:inline-flex;align-items:center;justify-content:center;gap:10px;
box-shadow:0 8px 20px rgba(17,96,190,.28)}
.btn:disabled{opacity:.7;cursor:default}
.btn svg{width:18px;height:18px;fill:#fff}
.hint{margin:12px 0 0;font-size:.85rem;color:$MUTED}
.spacer{flex:1}
.brand{margin-top:28px;display:flex;flex-direction:column;align-items:center;gap:6px}
.brand img{width:72px;height:auto;opacity:.95}
.brand span{font-size:.8rem;color:$MUTED;font-weight:600;letter-spacing:.02em}
.ups{font-size:2rem;color:$BLUE;margin:0 0 8px;font-weight:800}
""".trimIndent()

private fun brandFooter(): String = """
<div class="spacer"></div>
<div class="brand">
  <img src="$LOGO_SRC" alt="Timbre Bahia" width="72" height="68">
  <span>Timbre Bahia</span>
</div>
""".trimIndent()

private fun iconPhone(): String =
    """<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15.5 1h-7A2.5 2.5 0 0 0 6 3.5v17A2.5 2.5 0 0 0 8.5 23h7a2.5 2.5 0 0 0 2.5-2.5v-17A2.5 2.5 0 0 0 15.5 1zm-3.5 20a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5zM16 18H8V4h8v14z"/></svg>"""

private fun iconPin(): String =
    """<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5z"/></svg>"""

private fun iconShield(): String =
    """<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2 4 5v6c0 5.55 3.84 10.74 8 12 4.16-1.26 8-6.45 8-12V5l-8-3zm0 10.99h6c-.53 4.12-3.28 7.79-6 8.94V12H6V6.3l6-2.25v8.94z"/></svg>"""

private fun iconNav(): String =
    """<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2 4.5 20.29l.71.71L12 18l6.79 3 .71-.71L12 2z"/></svg>"""

private fun iconLock(): String =
    """<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10z"/></svg>"""

private fun iconWalk(): String =
    """<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M13.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM9.8 8.9 7 23h2.1l1.8-8 2.1 2v6h2v-7.5l-2.1-2 .6-3C14.8 12 16.8 13 19 13v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-.9-1.7-.9-.3 0-.5.1-.8.1L6 8.3V13h2V9.6l1.8-.7z"/></svg>"""

private fun iconQr(): String =
    """<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 11h8V3H3v8zm2-6h4v4H5V5zm8-2v8h8V3h-8zm6 6h-4V5h4v4zM3 21h8v-8H3v8zm2-6h4v4H5v-4zm13 2h2v2h-2zm-2-2h2v2h-2zm2 4h2v2h-2zm2-2h2v2h-2zm-6 0h2v2h-2zm2-2h2v2h-2zm2-2h2v2h-2z"/></svg>"""

private fun iconGps(): String =
    """<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3A8.994 8.994 0 0 0 13 3.06V1h-2v2.06A8.994 8.994 0 0 0 3.06 11H1v2h2.06A8.994 8.994 0 0 0 11 20.94V23h2v-2.06A8.994 8.994 0 0 0 20.94 13H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/></svg>"""

private fun iconX(): String =
    """<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"/></svg>"""

private fun mobileRequiredHtml(): String = """
<div class="wrap">
  <div class="icon">${iconPhone()}</div>
  <p class="ups">¡Ups!</p>
  <h1>Tenés que entrar desde un dispositivo móvil</h1>
  <p class="lead">Timbre Bahia para el portero por QR está optimizado para smartphones.</p>
  ${brandFooter()}
</div>
""".trimIndent()

private fun locationExplainInner(interactive: Boolean): String {
    val button = if (interactive) {
        """
        <button class="btn" type="button" id="tb-share-location">
          ${iconPin()}
          Compartir mi ubicación
        </button>
        <p class="hint">Solo se usará para esta verificación</p>
        """.trimIndent()
    } else {
        ""
    }
    return """
<div class="wrap">
  <div class="icon">${iconPin()}</div>
  <h1>Verificación de ubicación</h1>
  <p class="lead">Para tu seguridad y la del residente, necesitamos confirmar que estás en la dirección del edificio.</p>
  <div class="card">
    <div class="row"><div class="bullet">${iconShield()}</div><div>Protege al residente verificando quién toca</div></div>
    <div class="row"><div class="bullet">${iconNav()}</div><div>Solo funciona si estás en el lugar correcto</div></div>
    <div class="row"><div class="bullet">${iconLock()}</div><div>Tu ubicación no se guarda ni se comparte</div></div>
  </div>
  $button
  ${brandFooter()}
</div>
""".trimIndent()
}

private fun wrongLocationInner(denied: Boolean): String {
    val title: String
    val lead: String
    val rows: String
    if (denied) {
        title = "No pudimos obtener tu ubicación"
        lead = "Para tocar el timbre necesitamos permiso de ubicación y que estés en el edificio."
        rows = """
        <div class="row"><div class="bullet">${iconLock()}</div><div>Permití el acceso a la ubicación en el navegador</div></div>
        <div class="row"><div class="bullet">${iconWalk()}</div><div>Acercate a la puerta del edificio</div></div>
        <div class="row"><div class="bullet">${iconQr()}</div><div>Recargá o escaneá el QR de nuevo</div></div>
        """.trimIndent()
    } else {
        title = "No estás en la dirección correcta"
        lead = "Para poder tocar el timbre, necesitás estar físicamente en la ubicación del edificio."
        rows = """
        <div class="row"><div class="bullet">${iconWalk()}</div><div>Acercate a la puerta del edificio indicado</div></div>
        <div class="row"><div class="bullet">${iconQr()}</div><div>Escaneá el QR nuevamente cuando estés ahí</div></div>
        <div class="row"><div class="bullet">${iconGps()}</div><div>Asegurate de estar al aire libre o cerca de una ventana para mejor señal GPS</div></div>
        """.trimIndent()
    }
    val legend = if (denied) {
        """<div class="map-legend"><span><i class="building"></i>Edificio</span></div>"""
    } else {
        """<div class="map-legend"><span><i class="building"></i>Edificio</span><span><i class="you"></i>Tu ubicación</span></div>"""
    }
    return """
<div class="wrap">
  <div class="icon">${iconPin()}<span class="badge">${iconX()}</span></div>
  <h1>$title</h1>
  <p class="lead">$lead</p>
  <div class="map-wrap">
    <div id="tb-map" class="map" role="img" aria-label="Mapa de ubicación"></div>
    $legend
  </div>
  <div class="card">$rows</div>
  ${brandFooter()}
</div>
""".trimIndent()
}

private fun distanceMeters(a: LatLng, b: LocationGateTarget): Double {
    val dLat = (b.lat - a.lat).toRadians()
    val dLng = (b.lng - a.lng).toRadians()
    val lat1 = a.lat.toRadians()
    val lat2 = b.lat.toRadians()
    val h = sin(dLat / 2) * sin(dLat / 2) +
        cos(lat1) * cos(lat2) * sin(dLng / 2) * sin(dLng / 2)
    return 2 * EARTH_RADIUS_METERS * asin(min(1.0, sqrt(h)))
}

private fun Double.toRadians(): Double = this * PI / 180.0
